import React, { useEffect, useState, useCallback, useRef } from "react";
import { Panel } from "reactflow";
import {
  Minus,
  Plus,
  ChevronUp,
  ChevronDown,
  AlignVerticalJustifyStart,
  Clipboard,
} from "lucide-react";
import "./ZoomControls.css";
import { saveTriggerPositionForNode } from "@/lib/workflowGraphPersistence";

// Cap how far fitView can zoom in after auto-layout (1 = 100%)
const FIT_VIEW_MAX_ZOOM = 1;

const ZoomControls = ({
  reactFlowInstance,
  nodes,
  setNodes,
  edges,
  setEdges,
  currentWorkflow,
  workflowId: workflowIdProp,
  updateNode,
  onPersistNodeGraph,
  pasteNode,
  hasCopiedNode,
  getCopiedNodeInfo,
  csrf_token,
  toast,
}) => {
  const [zoomLevel, setZoomLevel] = useState(100);
  const [isExpanded, setIsExpanded] = useState(true);
  const [shiftState, setShiftState] = useState("none"); // "none", "single", "history", "double", or "double-history"
  const lastZoomUpdateTime = useRef(0);
  const zoomCheckInterval = useRef(null);
  const autoLayoutRunning = useRef(false);

  const resolveWorkflowId = useCallback(() => {
    if (currentWorkflow && typeof currentWorkflow === 'object' && currentWorkflow.id != null) {
      return String(currentWorkflow.id)
    }
    if (typeof currentWorkflow === 'string' || typeof currentWorkflow === 'number') {
      return String(currentWorkflow)
    }
    if (workflowIdProp != null) {
      return String(workflowIdProp)
    }
    return null
  }, [currentWorkflow, workflowIdProp])

  const collapseAllNodes = () => {
    const workflowId = resolveWorkflowId()
    if (!setNodes || !workflowId) return;
    
    setNodes((nds) => {
      const updatedNodes = nds.map((node) => ({
        ...node,
        data: {
          ...node.data,
          expanded: false,
        },
      }));

      updatedNodes.forEach((node) => {
        updateNode(workflowId, node.id, {
          data: { ...node.data, expanded: false },
        });
      });

      if (onPersistNodeGraph) {
        onPersistNodeGraph(updatedNodes);
      }

      return updatedNodes;
    });

    // Trigger auto layout to update spacing for collapsed nodes
    setTimeout(() => {
      autoLayoutSteps();
    }, 300);
  };

  const expandAllNodes = () => {
    const workflowId = resolveWorkflowId()
    if (!setNodes || !workflowId) return;

    setNodes((nds) => {
      const updatedNodes = nds.map((node) => ({
        ...node,
        data: {
          ...node.data,
          expanded: true,
        },
      }));

      updatedNodes.forEach((node) => {
        updateNode(workflowId, node.id, {
          data: { ...node.data, expanded: true },
        });
      });

      if (onPersistNodeGraph) {
        onPersistNodeGraph(updatedNodes);
      }

      return updatedNodes;
    });

    // Trigger auto layout to update spacing for expanded nodes
    setTimeout(() => {
      autoLayoutSteps();
    }, 300);
  };

  const handleZoomChange = useCallback(
    (newZoom) => {
      const now = Date.now();
      if (now - lastZoomUpdateTime.current < 300) return;
      lastZoomUpdateTime.current = now;
      const newZoomLevel = Math.round(newZoom * 100);
      setZoomLevel(newZoomLevel);
      // Removed automatic expand/collapse logic - nodes now only change manually
    },
    []
  );

  // Update zoom level when component mounts or reactFlowInstance changes
  useEffect(() => {
    if (!reactFlowInstance) return;
    // Don't reset zoom, just get the current zoom level
    const currentZoom = reactFlowInstance.getZoom();
    setZoomLevel(Math.round(currentZoom * 100));

    // Use a debounced function to check zoom level
    let timeoutId = null;
    const checkZoomDebounced = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        if (reactFlowInstance) {
          const currentZoom = reactFlowInstance.getZoom();
          handleZoomChange(currentZoom);
        }
      }, 100);
    };

    // Use a less frequent interval to check zoom level
    zoomCheckInterval.current = setInterval(checkZoomDebounced, 500);

    return () => {
      if (zoomCheckInterval.current) {
        clearInterval(zoomCheckInterval.current);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [reactFlowInstance, handleZoomChange]);

  // Check if any side panels are open and adjust position accordingly
  useEffect(() => {
    const checkSidePanels = () => {
      // Robustly check for visible panels
      const isVisible = (selector) => {
        const el = document.querySelector(selector);
        if (!el) return false;
        const style = window.getComputedStyle(el);
        return (
          el.offsetParent !== null &&
          style.display !== "none" &&
          style.visibility !== "hidden"
        );
      };
      const blocksPanel = isVisible(".blocks-panel");
      const triggersPanel = isVisible(".triggers-panel");
      const triggersConfigPanel = isVisible(".triggers-panel .trigger-config");
      const outlinePanel = isVisible(".outline-panel");
      const historyPanel = isVisible(".workflow-history-panel");

      // Count panels: triggersPanel + triggersConfigPanel (if open) count as two
      let openPanels = 0;
      if (blocksPanel) openPanels++;
      if (triggersPanel) openPanels++;
      if (triggersConfigPanel) openPanels++;
      if (outlinePanel) openPanels++;
      if (historyPanel) openPanels++;

      // But if triggersPanel and triggersConfigPanel are both open, only count triggersPanel once, but add one if config is open
      if (triggersPanel && triggersConfigPanel) openPanels++; // triggersPanel + config = 2

      // Set shiftState based on number of open panels and their widths
      if (openPanels === 0) {
        setShiftState("none");
      } else if (openPanels === 1) {
        // Check if the single open panel is the history panel (400px) or others (260px)
        if (historyPanel) {
          setShiftState("history"); // Special state for the wider history panel
        } else {
          setShiftState("single");
        }
      } else {
        // For multiple panels, check if history panel is one of them
        if (historyPanel) {
          setShiftState("double-history"); // Special state when history panel is open with others
        } else {
          setShiftState("double");
        }
      }
    };

    // Set up a mutation observer to detect DOM changes
    const observer = new MutationObserver(() => {
      // Use requestAnimationFrame to ensure we're checking after the DOM has been updated
      requestAnimationFrame(checkSidePanels);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "hidden", "data-state"],
      characterData: false,
      characterDataOldValue: false,
    });

    // Initial check on mount
    checkSidePanels();

    return () => observer.disconnect();
  }, []);

  const zoomIn = useCallback(() => {
    if (!reactFlowInstance) return;
    const newZoom = reactFlowInstance.getZoom() + 0.1;
    reactFlowInstance.zoomTo(newZoom);
    setZoomLevel(Math.round(newZoom * 100));
  }, [reactFlowInstance]);

  const zoomOut = useCallback(() => {
    if (!reactFlowInstance) return;
    const currentZoom = reactFlowInstance.getZoom();
    const newZoom = currentZoom - 0.1;
    reactFlowInstance.zoomTo(newZoom);
    setZoomLevel(Math.round(newZoom * 100));
  }, [reactFlowInstance]);

  const resetZoom = useCallback(() => {
    if (!reactFlowInstance) return;
    reactFlowInstance.zoomTo(1.0, { duration: 300 });
    setZoomLevel(100);
  }, [reactFlowInstance]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handlePaste = async () => {
    const workflowId = resolveWorkflowId()
    if (!workflowId) {
      toast({
        title: "Error",
        description: "No workflow selected",
        variant: "destructive"
      })
      return
    }

    if (!hasCopiedNode()) {
      toast({
        title: "Nothing to Paste",
        description: "No node copied to clipboard",
        variant: "destructive"
      })
      return
    }

    const copiedInfo = getCopiedNodeInfo()
    if (!copiedInfo) {
      toast({
        title: "Clipboard Empty",
        description: "No valid node in clipboard",
        variant: "destructive"
      })
      return
    }

    // Calculate position for pasted node (center of viewport)
    const position = { x: 200, y: 100 } // Default position

    try {
      const pastedNode = await pasteNode(workflowId, position, csrf_token)
      
      if (pastedNode) {
        toast({
          title: "Node Pasted",
          description: `${copiedInfo.nodeName} has been pasted`,
        })
      } else {
        toast({
          title: "Paste Failed",
          description: "Failed to paste node",
          variant: "destructive"
        })
      }
    } catch (error) {
      console.error('Paste error:', error)
      toast({
        title: "Paste Failed",
        description: "An error occurred while pasting",
        variant: "destructive"
      })
    }
  };

  const autoLayoutSteps = useCallback(async () => {
    const workflowId = resolveWorkflowId()
    if (!setNodes || !workflowId || !reactFlowInstance) {
      return;
    }

    // Get the current nodes from React Flow instance to ensure we have the latest state
    const currentNodes = reactFlowInstance.getNodes();
    
    if (!currentNodes || currentNodes.length === 0) {
      return;
    }

    // Prevent infinite loops - don't run if already running
    if (autoLayoutRunning.current) {
      return;
    }
    autoLayoutRunning.current = true;

    try {
    // Find the trigger node (first node with type 'triggerNode')
    const triggerNode = currentNodes.find(node => node.type === 'triggerNode');
    if (!triggerNode) {
      return;
    }

    // Find all non-trigger nodes (workflow steps)
    const stepNodes = currentNodes.filter(node => node.type !== 'triggerNode');
    if (stepNodes.length === 0) {
      return;
    }
    
    // Check if nodes are expanded or collapsed to determine spacing
    const areNodesExpanded = stepNodes.length > 0 ? stepNodes[0].data?.expanded !== false : true;
    // Layout configuration - dynamic based on expansion state
    const triggerToStepSpacing = areNodesExpanded ? 120 : 60; // Keep expanded mode tighter under the trigger
    const horizontalSpacing = 125; // Distance between separate chains horizontally (decreased)
    const stepNodeWidth = 250; // Estimated width of each step node
    const estimatedNodeHeight = areNodesExpanded ? 120 : 80;
    const nodeMap = Object.fromEntries(currentNodes.map(node => [node.id, node]));

    // Use real rendered heights when available so layout is based on each node's bottom edge.
    const getNodeHeight = (nodeId) => {
      const node = nodeMap[nodeId];
      if (!node) return estimatedNodeHeight;
      return (
        node.measured?.height ||
        node.height ||
        node.__rf?.height ||
        estimatedNodeHeight
      );
    };

    const getNodeBottom = (nodeId) => {
      const position = nodePositions[nodeId] || nodeMap[nodeId]?.position;
      if (!position) return 0;
      return position.y + getNodeHeight(nodeId);
    };
    
    // Gap configuration based on source node type
    const getGapForNodeType = (nodeType) => {
      if (!areNodesExpanded) return 50; // Collapsed nodes use smaller gap
      // TODO: if there are 5 or more stepNodes, then the gap under the trigger should be 1000
      
      switch (nodeType) {
        case 'taskNode':
          return 120;
        case 'requestNode':
          return 90;
        case 'formNode':
          return 100; // Form nodes still get slightly more spacing
        default:
          return 95; // Default gap for other node types
      }
    };
    
    // Build parent/children maps for the full graph (trigger + step nodes)
    const parentsMap = { [triggerNode.id]: [] };
    const childrenMap = { [triggerNode.id]: [] };
    stepNodes.forEach(n => {
      parentsMap[n.id] = [];
      childrenMap[n.id] = [];
    });
    if (edges && edges.length > 0) {
      edges.forEach(edge => {
        if (childrenMap[edge.source] !== undefined && parentsMap[edge.target] !== undefined) {
          if (!childrenMap[edge.source].includes(edge.target)) {
            childrenMap[edge.source].push(edge.target);
          }
          if (!parentsMap[edge.target].includes(edge.source)) {
            parentsMap[edge.target].push(edge.source);
          }
        }
      });
    }


    // Find connected component reachable from the trigger (BFS following children)
    const connectedToTrigger = new Set([triggerNode.id]);
    const bfsQueue = [triggerNode.id];
    while (bfsQueue.length > 0) {
      const id = bfsQueue.shift();
      (childrenMap[id] || []).forEach(child => {
        if (!connectedToTrigger.has(child)) {
          connectedToTrigger.add(child);
          bfsQueue.push(child);
        }
      });
    }

    // Compute level (longest path from trigger) and topological order via Kahn's
    const level = { [triggerNode.id]: 0 };
    const inDegree = {};
    connectedToTrigger.forEach(id => { inDegree[id] = 0; });
    connectedToTrigger.forEach(id => {
      (childrenMap[id] || []).forEach(child => {
        if (connectedToTrigger.has(child)) {
          inDegree[child] = (inDegree[child] || 0) + 1;
        }
      });
    });
    const topoQueue = [];
    Object.keys(inDegree).forEach(id => {
      if (inDegree[id] === 0) {
        topoQueue.push(id);
        if (level[id] === undefined) level[id] = 0;
      }
    });
    const topoOrder = [];
    while (topoQueue.length > 0) {
      const id = topoQueue.shift();
      topoOrder.push(id);
      (childrenMap[id] || []).forEach(child => {
        if (!connectedToTrigger.has(child)) return;
        // Longest path from any root = vertical rank
        level[child] = Math.max(level[child] || 0, (level[id] || 0) + 1);
        inDegree[child]--;
        if (inDegree[child] === 0) topoQueue.push(child);
      });
    }

    // Generate a unique key to force re-render
    const renderKey = Date.now();

    // Calculate positions for all nodes
    const nodePositions = {};
    // Keep trigger in its current position
    nodePositions[triggerNode.id] = { ...triggerNode.position };

    // Track placed x-ranges per level for collision avoidance
    const placedRangesAtLevel = {};

    // For multi-parent override behavior, pick one anchor parent:
    // the parent highest on screen (smallest Y).
    const getAnchorParentId = (nodeId) => {
      const parentIds = parentsMap[nodeId] || [];
      if (parentIds.length === 0) return null;
      if (parentIds.length === 1) return parentIds[0];

      let chosenParentId = parentIds[0];
      let chosenY = nodePositions[chosenParentId]?.y ?? nodeMap[chosenParentId]?.position?.y ?? Number.POSITIVE_INFINITY;

      parentIds.forEach(parentId => {
        const parentY = nodePositions[parentId]?.y ?? nodeMap[parentId]?.position?.y ?? Number.POSITIVE_INFINITY;
        if (parentY < chosenY) {
          chosenY = parentY;
          chosenParentId = parentId;
        }
      });

      return chosenParentId;
    };

    // A parent should only consider a multi-parent child if this parent
    // is the chosen anchor parent for that child.
    const isEffectiveChildOfParent = (childId, parentId) => {
      const parentIds = parentsMap[childId] || [];
      if (parentIds.length === 0) return false;
      if (parentIds.length === 1) return parentIds[0] === parentId;
      return getAnchorParentId(childId) === parentId;
    };

    const getEffectiveChildren = (parentId) =>
      (childrenMap[parentId] || []).filter(childId => isEffectiveChildOfParent(childId, parentId));

    // Compute Y for a node based on its parents' positions and types
    const computeY = (nodeId) => {
      const myParents = parentsMap[nodeId] || [];
      if (myParents.length === 0) {
        return getNodeBottom(triggerNode.id) + triggerToStepSpacing;
      }
      let maxParentBottom = -Infinity;
      let dominantParentType = null;
      myParents.forEach(pid => {
        const pNode = nodeMap[pid];
        const parentBottom = getNodeBottom(pid);
        if (parentBottom > maxParentBottom) {
          maxParentBottom = parentBottom;
          dominantParentType = pNode?.type;
        }
      });
      if (maxParentBottom === -Infinity) {
        return getNodeBottom(triggerNode.id) + triggerToStepSpacing;
      }
      if (dominantParentType === 'triggerNode') {
        return maxParentBottom + triggerToStepSpacing;
      }
      return maxParentBottom + getGapForNodeType(dominantParentType);
    };

    // Compute desired X for a node based on its parents
    const computeDesiredX = (nodeId) => {
      const myParents = parentsMap[nodeId] || [];
      if (myParents.length === 0) {
        return triggerNode.position.x;
      }
      if (myParents.length === 1) {
        const parentId = myParents[0];
        const parentPos = nodePositions[parentId] || triggerNode.position;
        const siblings = getEffectiveChildren(parentId).filter(c => connectedToTrigger.has(c));
        if (siblings.length <= 1) {
          // Single child: stack directly under parent
          return parentPos.x;
        }
        // Multi-child fan-out (e.g. if/else branches): spread siblings horizontally, centered under parent
        const idx = siblings.indexOf(nodeId);
        const totalWidth = (siblings.length - 1) * (stepNodeWidth + horizontalSpacing);
        const startX = parentPos.x - totalWidth / 2;
        return startX + idx * (stepNodeWidth + horizontalSpacing);
      }
      // Multi-parent merge: centroid of parent x positions
      const sumX = myParents.reduce((acc, pid) => acc + (nodePositions[pid]?.x || 0), 0);
      return sumX / myParents.length;
    };

    // Place a node at desired X, shifting right to avoid same-level overlaps
    const placeAt = (desiredX, lvl) => {
      const half = stepNodeWidth / 2;
      let x = desiredX;
      const placed = placedRangesAtLevel[lvl] || [];
      let collided = true;
      let safety = 0;
      while (collided && safety < 200) {
        collided = false;
        for (const p of placed) {
          if (x - half < p.maxX + horizontalSpacing && x + half > p.minX - horizontalSpacing) {
            x = p.maxX + horizontalSpacing + half;
            collided = true;
            break;
          }
        }
        safety++;
      }
      placed.push({ minX: x - half, maxX: x + half });
      placed.sort((a, b) => a.minX - b.minX);
      placedRangesAtLevel[lvl] = placed;
      return x;
    };

    // Place connected step nodes in topological order
    topoOrder.forEach(id => {
      if (id === triggerNode.id) return;
      const desiredX = computeDesiredX(id);
      const y = computeY(id);
      const lvl = level[id] || 0;
      const finalX = placeAt(desiredX, lvl);
      nodePositions[id] = { x: finalX, y };
    });


    // Side chains: disconnected nodes (not reachable from trigger) — preserve existing right-side behavior
    const buildSideChain = (startNodeId, visited) => {
      if (visited.has(startNodeId)) return [];
      visited.add(startNodeId);
      const chain = [startNodeId];
      const ch = childrenMap[startNodeId] || [];
      ch.forEach(childId => {
        const isStepNode = stepNodes.find(n => n.id === childId);
        const incomingCount = (parentsMap[childId] || []).length;
        if (isStepNode && incomingCount === 1 && !visited.has(childId)) {
          chain.push(...buildSideChain(childId, visited));
        }
      });
      return chain;
    };

    const disconnectedNodes = stepNodes.filter(n => !connectedToTrigger.has(n.id));
    const sideChains = [];
    const sideProcessed = new Set();
    disconnectedNodes.forEach(node => {
      if (!sideProcessed.has(node.id)) {
        const chain = buildSideChain(node.id, new Set([...sideProcessed]));
        if (chain.length > 0) {
          sideChains.push(chain);
          chain.forEach(id => sideProcessed.add(id));
        }
      }
    });

    if (sideChains.length > 0) {
      // Find rightmost X used by main layout to position side chains beyond it
      let mainRightmost = triggerNode.position.x;
      Object.keys(nodePositions).forEach(id => {
        const pos = nodePositions[id];
        if (pos && pos.x > mainRightmost) mainRightmost = pos.x;
      });
      const sideChainsStartX = mainRightmost + (stepNodeWidth + horizontalSpacing) * 2;
      const sideStartY = getNodeBottom(triggerNode.id) + triggerToStepSpacing;

      sideChains.forEach((chain, chainIndex) => {
        const chainX = sideChainsStartX + (chainIndex * (stepNodeWidth + horizontalSpacing));
        let currentY = sideStartY;
        chain.forEach((nodeId, nodeIndex) => {
          nodePositions[nodeId] = { x: chainX, y: currentY };
          if (nodeIndex < chain.length - 1) {
            const sourceNode = nodeMap[nodeId];
            const gapForThisNode = getGapForNodeType(sourceNode?.type);
            currentY = currentY + getNodeHeight(nodeId) + gapForThisNode;
          }
        });
      });
    }

    // Apply condition-column alignment for all If/Else nodes (including disconnected ones):
    // - condition #1 starts at next column to the right of the if/else node
    // - remaining conditions continue to the right
    // - single-parent descendants inherit that condition column
    const outgoingEdgesBySource = {};
    (edges || []).forEach(edge => {
      if (!outgoingEdgesBySource[edge.source]) outgoingEdgesBySource[edge.source] = [];
      const exists = outgoingEdgesBySource[edge.source].some(existing =>
        existing.target === edge.target &&
        existing.sourceHandle === edge.sourceHandle &&
        existing.targetHandle === edge.targetHandle
      );
      if (!exists) {
        outgoingEdgesBySource[edge.source].push(edge);
      }
    });

    const getIfElseHandleOrder = (ifElseNode) => {
      const configuredHandles = Array.isArray(ifElseNode?.data?.conditions)
        ? ifElseNode.data.conditions
            .map(condition => condition?.id)
            .filter(Boolean)
            .map(conditionId => `output-${conditionId}`)
        : [];

      const usedHandles = ((outgoingEdgesBySource[ifElseNode.id] || [])
        .filter(edge => typeof edge.sourceHandle === 'string' && edge.sourceHandle.startsWith('output-'))
        .map(edge => edge.sourceHandle));

      const uniqueUsedHandles = [...new Set(usedHandles)];
      if (configuredHandles.length === 0) return uniqueUsedHandles;

      const knownHandles = configuredHandles.filter(handle => uniqueUsedHandles.includes(handle));
      const unknownHandles = uniqueUsedHandles.filter(handle => !knownHandles.includes(handle));
      return [...knownHandles, ...unknownHandles];
    };

    // Generic parent->children alignment:
    // - one direct child: center under parent
    // - multiple direct children: same row, centered group under parent
    Object.keys(childrenMap).forEach(parentId => {
      if (!nodePositions[parentId]) return;

      const directChildren = (childrenMap[parentId] || [])
        .filter(childId => nodePositions[childId] && (parentsMap[childId] || []).length === 1);
      if (directChildren.length === 0) return;

      const parentX = nodePositions[parentId].x;
      const parentBottom = getNodeBottom(parentId);
      const parentType = nodeMap[parentId]?.type;
      const rowY = parentBottom + (parentType === 'triggerNode' ? triggerToStepSpacing : getGapForNodeType(parentType));

      let orderedChildren = [...directChildren];
      // Keep if/else condition order deterministic by handle order.
      if (nodeMap[parentId]?.type === 'ifElseNode') {
        const handleOrder = getIfElseHandleOrder(nodeMap[parentId]);
        const edgeByTarget = {};
        (outgoingEdgesBySource[parentId] || []).forEach(edge => {
          if (!edgeByTarget[edge.target]) edgeByTarget[edge.target] = edge;
        });
        orderedChildren.sort((a, b) => {
          const aHandle = edgeByTarget[a]?.sourceHandle;
          const bHandle = edgeByTarget[b]?.sourceHandle;
          const aIdx = handleOrder.indexOf(aHandle);
          const bIdx = handleOrder.indexOf(bHandle);
          const normA = aIdx === -1 ? Number.MAX_SAFE_INTEGER : aIdx;
          const normB = bIdx === -1 ? Number.MAX_SAFE_INTEGER : bIdx;
          if (normA !== normB) return normA - normB;
          return (nodePositions[a].x || 0) - (nodePositions[b].x || 0);
        });
      } else {
        orderedChildren.sort((a, b) => (nodePositions[a].x || 0) - (nodePositions[b].x || 0));
      }

      if (orderedChildren.length === 1) {
        const childId = orderedChildren[0];
        nodePositions[childId] = {
          ...nodePositions[childId],
          x: parentX,
          y: rowY
        };
        return;
      }

      const spacing = stepNodeWidth + horizontalSpacing;
      const totalWidth = (orderedChildren.length - 1) * spacing;
      const startX = parentX - (totalWidth / 2);
      orderedChildren.forEach((childId, index) => {
        nodePositions[childId] = {
          ...nodePositions[childId],
          x: startX + (index * spacing),
          y: rowY
        };
      });
    });


    // Multi-parent child override:
    // Position only the child under the anchor parent. Do not reflow parents/upstream.
    stepNodes.forEach(node => {
      const parentIds = parentsMap[node.id] || [];
      if (parentIds.length <= 1) return;
      if (!nodePositions[node.id]) return;

      const anchorParentId = getAnchorParentId(node.id);
      if (!anchorParentId || !nodePositions[anchorParentId]) return;

      const anchorX = nodePositions[anchorParentId].x;
      const anchorBottom = getNodeBottom(anchorParentId);
      const anchorType = nodeMap[anchorParentId]?.type;
      const childY = anchorBottom + (anchorType === 'triggerNode' ? triggerToStepSpacing : getGapForNodeType(anchorType));

      let childX = anchorX;

      // If anchored to an if/else parent, follow the same branch grouping lane
      // logic as direct if/else children (instead of always centering).
      if (anchorType === 'ifElseNode') {
        const anchorChildren = (childrenMap[anchorParentId] || [])
          .filter(childId => nodePositions[childId] && getAnchorParentId(childId) === anchorParentId);

        let orderedChildren = [...anchorChildren];
        const handleOrder = getIfElseHandleOrder(nodeMap[anchorParentId]);
        const edgeByTarget = {};
        (outgoingEdgesBySource[anchorParentId] || []).forEach(edge => {
          if (!edgeByTarget[edge.target]) edgeByTarget[edge.target] = edge;
        });

        orderedChildren.sort((a, b) => {
          const aHandle = edgeByTarget[a]?.sourceHandle;
          const bHandle = edgeByTarget[b]?.sourceHandle;
          const aIdx = handleOrder.indexOf(aHandle);
          const bIdx = handleOrder.indexOf(bHandle);
          const normA = aIdx === -1 ? Number.MAX_SAFE_INTEGER : aIdx;
          const normB = bIdx === -1 ? Number.MAX_SAFE_INTEGER : bIdx;
          if (normA !== normB) return normA - normB;
          return (nodePositions[a].x || 0) - (nodePositions[b].x || 0);
        });

        const childIndex = orderedChildren.indexOf(node.id);
        if (childIndex !== -1) {
          if (orderedChildren.length === 1) {
            childX = anchorX;
          } else {
            const spacing = stepNodeWidth + horizontalSpacing;
            const totalWidth = (orderedChildren.length - 1) * spacing;
            const startX = anchorX - (totalWidth / 2);
            childX = startX + (childIndex * spacing);
          }
        }
      }

      nodePositions[node.id] = {
        ...nodePositions[node.id],
        x: childX,
        y: childY
      };
    });


    // Re-group if/else children after multi-parent override so condition siblings
    // stay aligned together (instead of leaving single-parent condition nodes centered).
    stepNodes.forEach(ifElseNode => {
      if (ifElseNode.type !== 'ifElseNode') return;
      if (!nodePositions[ifElseNode.id]) return;

      const handleOrder = getIfElseHandleOrder(ifElseNode);
      const rawTargets = (outgoingEdgesBySource[ifElseNode.id] || []).map(edge => edge.target);
      const uniqueTargets = [...new Set(rawTargets)].filter(targetId => {
        if (!nodePositions[targetId]) return false;
        const parentCount = (parentsMap[targetId] || []).length;
        return parentCount === 1 || getAnchorParentId(targetId) === ifElseNode.id;
      });

      if (uniqueTargets.length === 0) return;

      const edgeByTarget = {};
      (outgoingEdgesBySource[ifElseNode.id] || []).forEach(edge => {
        if (!edgeByTarget[edge.target]) edgeByTarget[edge.target] = edge;
      });

      const orderedChildren = [...uniqueTargets].sort((a, b) => {
        const aHandle = edgeByTarget[a]?.sourceHandle;
        const bHandle = edgeByTarget[b]?.sourceHandle;
        const aIdx = handleOrder.indexOf(aHandle);
        const bIdx = handleOrder.indexOf(bHandle);
        const normA = aIdx === -1 ? Number.MAX_SAFE_INTEGER : aIdx;
        const normB = bIdx === -1 ? Number.MAX_SAFE_INTEGER : bIdx;
        if (normA !== normB) return normA - normB;
        return (nodePositions[a].x || 0) - (nodePositions[b].x || 0);
      });

      const parentX = nodePositions[ifElseNode.id].x;
      const parentBottom = getNodeBottom(ifElseNode.id);
      const rowY = parentBottom + getGapForNodeType(ifElseNode.type);

      if (orderedChildren.length === 1) {
        const onlyChildId = orderedChildren[0];
        nodePositions[onlyChildId] = {
          ...nodePositions[onlyChildId],
          x: parentX,
          y: rowY
        };
        return;
      }

      const spacing = stepNodeWidth + horizontalSpacing;
      const totalWidth = (orderedChildren.length - 1) * spacing;
      const startX = parentX - (totalWidth / 2);
      orderedChildren.forEach((childId, index) => {
        nodePositions[childId] = {
          ...nodePositions[childId],
          x: startX + (index * spacing),
          y: rowY
        };
      });
    });

    // Final single-parent normalization:
    // after if/else regroup and multi-parent overrides, ensure true single-parent
    // children are still centered/grouped under their direct parent.
    Object.keys(childrenMap).forEach(parentId => {
      if (!nodePositions[parentId]) return;
      // If/Else parent children are already grouped by condition order above.
      // Skip generic single-parent normalization so we don't collapse condition groups.
      if (nodeMap[parentId]?.type === 'ifElseNode') return;

      const singleParentChildren = (childrenMap[parentId] || [])
        .filter(childId => {
          if (!nodePositions[childId]) return false;
          const parentIds = parentsMap[childId] || [];
          return parentIds.length === 1 && parentIds[0] === parentId;
        });

      if (singleParentChildren.length === 0) return;

      const parentX = nodePositions[parentId].x;
      const parentBottom = getNodeBottom(parentId);
      const parentType = nodeMap[parentId]?.type;
      const rowY = parentBottom + (parentType === 'triggerNode' ? triggerToStepSpacing : getGapForNodeType(parentType));

      if (singleParentChildren.length === 1) {
        const onlyChildId = singleParentChildren[0];
        nodePositions[onlyChildId] = {
          ...nodePositions[onlyChildId],
          x: parentX,
          y: rowY
        };
        return;
      }

      const orderedChildren = [...singleParentChildren].sort((a, b) => (nodePositions[a].x || 0) - (nodePositions[b].x || 0));
      const spacing = stepNodeWidth + horizontalSpacing;
      const totalWidth = (orderedChildren.length - 1) * spacing;
      const startX = parentX - (totalWidth / 2);
      orderedChildren.forEach((childId, index) => {
        nodePositions[childId] = {
          ...nodePositions[childId],
          x: startX + (index * spacing),
          y: rowY
        };
      });
    });

    // Final guardrail: keep disconnected flow(s) from overlapping trigger-connected flow.
    const connectedNodeIds = stepNodes
      .filter(node => connectedToTrigger.has(node.id))
      .map(node => node.id);
    const disconnectedNodeIds = stepNodes
      .filter(node => !connectedToTrigger.has(node.id))
      .map(node => node.id);

    if (connectedNodeIds.length > 0 && disconnectedNodeIds.length > 0) {
      const connectedRightmostX = connectedNodeIds.reduce((maxX, nodeId) => {
        const pos = nodePositions[nodeId];
        return pos ? Math.max(maxX, pos.x) : maxX;
      }, triggerNode.position.x);

      const disconnectedLeftmostX = disconnectedNodeIds.reduce((minX, nodeId) => {
        const pos = nodePositions[nodeId];
        return pos ? Math.min(minX, pos.x) : minX;
      }, Number.POSITIVE_INFINITY);

      // Keep at least one node-width + spacing buffer between domains.
      const minimumDisconnectedStartX = connectedRightmostX + stepNodeWidth + horizontalSpacing;
      if (Number.isFinite(disconnectedLeftmostX) && disconnectedLeftmostX < minimumDisconnectedStartX) {
        const shiftDelta = minimumDisconnectedStartX - disconnectedLeftmostX;
        disconnectedNodeIds.forEach(nodeId => {
          const pos = nodePositions[nodeId];
          if (!pos) return;
          nodePositions[nodeId] = {
            ...pos,
            x: pos.x + shiftDelta
          };
        });
      }
    }

    // Calculate new positions for step nodes and set handle configurations
    const updatedNodes = currentNodes.map(node => {
      if (node.type === 'triggerNode') {
        // Keep trigger node in its current position but set handle to bottom
        return {
          ...node,
          data: {
            ...node.data,
            autoLayoutMode: true, // Flag to indicate auto-layout is active
            handlePosition: 'bottom', // Force handle to bottom for auto-layout
            renderKey: renderKey // Force re-render
          }
        };
      } else {
        // Position step nodes based on calculated positions
        const newPosition = nodePositions[node.id] || node.position;

        return {
          ...node,
          position: newPosition,
          data: {
            ...node.data,
            autoLayoutMode: true, // Flag to indicate auto-layout is active
            handlePosition: node.type === 'requestNode' ? 'top-bottom' : 'top-bottom', // Force handles to top and bottom for auto-layout
            renderKey: renderKey // Force re-render
          }
        };
      }
    });

    // Force complete re-render by temporarily clearing and re-setting nodes
    setNodes([]);
    setTimeout(() => {
      setNodes(updatedNodes);
    }, 10);

    // Force ReactFlow to refresh by triggering a viewport update
    if (reactFlowInstance) {
      setTimeout(() => {
        const viewport = reactFlowInstance.getViewport();
        reactFlowInstance.setViewport({ ...viewport });
      }, 50);
    }

    // Wait for nodes to re-render with new handle positions, then update edges
    setTimeout(() => {
      const updatedEdges = edges.map(edge => {
        // Find source and target nodes
        const sourceNode = updatedNodes.find(n => n.id === edge.source);
        const targetNode = updatedNodes.find(n => n.id === edge.target);
        
        if (!sourceNode || !targetNode) return edge;
        
        // Update edge handles based on auto-layout positions
        let newSourceHandle = edge.sourceHandle;
        let newTargetHandle = edge.targetHandle;
        
        // If source is trigger node with auto-layout, use bottom handle
        if (sourceNode.type === 'triggerNode' && sourceNode.data.autoLayoutMode) {
          newSourceHandle = 'a'; // Bottom handle
        }
        
        // If target is step node with auto-layout, use top handle
        if (targetNode.type !== 'triggerNode' && targetNode.data.autoLayoutMode) {
          newTargetHandle = 'b'; // Top handle
        }
        
        // If source is step node with auto-layout, use bottom handle.
        // Preserve if/else branch handles (output-<conditionId>) so branch edges stay attached.
        if (sourceNode.type !== 'triggerNode' && sourceNode.type !== 'ifElseNode' && sourceNode.data.autoLayoutMode) {
          newSourceHandle = 'a'; // Bottom handle
        }
        
        // Special handling for request nodes - ensure they use top-bottom handles
        if (sourceNode.type === 'requestNode' && sourceNode.data.autoLayoutMode) {
          newSourceHandle = 'a'; // Bottom handle for request nodes
        }
        if (targetNode.type === 'requestNode' && targetNode.data.autoLayoutMode) {
          newTargetHandle = 'b'; // Top handle for request nodes
        }
        
        return {
          ...edge,
          sourceHandle: newSourceHandle,
          targetHandle: newTargetHandle
        };
      });

      // Update edges after nodes have re-rendered
      setEdges(updatedEdges);
      
      // Force another viewport refresh to ensure everything is properly rendered
      if (reactFlowInstance) {
        const viewport = reactFlowInstance.getViewport();
        reactFlowInstance.setViewport({ ...viewport });
      }
    }, 150);

    // OPTIMIZATION: Update all nodes in UI immediately for instant visual feedback
    const allUpdates = [];
    
    // Prepare trigger node update
    const triggerPosition = nodePositions[triggerNode.id];
    const updatedTriggerData = {
      ...triggerNode.data,
      autoLayoutMode: true,
      handlePosition: 'bottom',
      expanded: triggerNode.data.expanded
    };
    
    allUpdates.push({
      nodeId: triggerNode.id,
      position: triggerPosition,
      data: updatedTriggerData
    });
    
    // Prepare all step node updates
    stepNodes.forEach(node => {
      const newPosition = nodePositions[node.id] || node.position;
      const updatedNodeData = {
        ...node.data,
        autoLayoutMode: true,
        handlePosition:'top-bottom',
        expanded: node.data.expanded
      };
      
      allUpdates.push({
        nodeId: node.id,
        position: newPosition,
        data: updatedNodeData
      });
    });

    // Update all nodes in UI immediately (instant visual feedback) - ONLY position updates
    allUpdates.forEach(update => {
      updateNode(workflowId, update.nodeId, {
        position: update.position
      });
    });
    
    // Persist layout positions via the same endpoint used for expanded state.
    const nodesForPersist = currentNodes.map((node) => {
      const newPosition = nodePositions[node.id] || node.position
      const isTrigger = node.type === 'triggerNode'

      return {
        ...node,
        position: newPosition,
        data: {
          ...node.data,
          autoLayoutMode: true,
          handlePosition: isTrigger ? 'bottom' : 'top-bottom',
        },
      }
    })

    if (onPersistNodeGraph) {
      onPersistNodeGraph(nodesForPersist)
    } else {
      const savePromises = allUpdates.map((update) =>
        saveNodeToDatabase(update.nodeId, update.position),
      )

      Promise.allSettled(savePromises).then((results) => {
        const failedSaves = results.filter((result) => result.status === 'rejected')
        if (failedSaves.length > 0) {
          console.warn(`${failedSaves.length} node saves failed during auto layout`)
        }
      })
    }

    // Fit all nodes into view after auto layout
    setTimeout(() => {
      if (reactFlowInstance) {
        reactFlowInstance.fitView({
          padding: 0.2,
          includeHiddenNodes: false,
          maxZoom: FIT_VIEW_MAX_ZOOM,
          duration: 800,
        });
      }
    }, 200); // Wait for nodes to be fully updated

    // Helper function to save node position to database (position-only, like drag handler)
    async function saveNodeToDatabase(nodeId, position) {
      if (!workflowId) return;

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || 
                         document.querySelector('input[name="_token"]')?.value ||
                         'fallback-token';

        // Check if it's a task node (step) or trigger node
        if (nodeId.startsWith('step-')) {
          const stepId = nodeId.replace('step-', '');
          
          // Use the position-specific endpoint like the drag handler does
          const response = await fetch(`/workflows/${workflowId}/steps/${stepId}/position`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-TOKEN': csrfToken,
              'Accept': 'application/json',
            },
            body: JSON.stringify({
              position: JSON.stringify(position)
            }),
          });

          if (!response.ok) {
            throw new Error(`Failed to update step position: ${response.status}`);
          }
        } else if (nodeId.startsWith('taskNode-') || nodeId.startsWith('formNode-') || nodeId.startsWith('requestNode-') || 
                   nodeId.startsWith('actionNode-') || nodeId.startsWith('dataNode-') || nodeId.startsWith('aiNode-') || 
                   nodeId.startsWith('responseNode-') || nodeId.startsWith('workflowNode-') || nodeId.startsWith('branchNode-') ||
                   nodeId.startsWith('ifElseNode-') || nodeId.startsWith('placeholderNode-')) {
          // Handle various node types by finding the database ID
          const node = currentNodes.find(n => n.id === nodeId);
          if (node && node.databaseId) {
            const stepId = node.databaseId;
            
            // Use the position-specific endpoint like the drag handler does
            const response = await fetch(`/workflows/${workflowId}/steps/${stepId}/position`, {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrfToken,
                'Accept': 'application/json',
              },
              body: JSON.stringify({
                position: JSON.stringify(position)
              }),
            });

            if (!response.ok) {
              throw new Error(`Failed to update step position: ${response.status}`);
            }
          } else {
            console.warn(`No database ID found for ${nodeId.split('-')[0]} node: ${nodeId}`);
          }
        } else if (nodeId.startsWith('trigger-') || nodeId === 'start-trigger') {
          await saveTriggerPositionForNode(workflowId, nodeId, position, csrfToken)
        } else {
          // Unknown node type, skip database save
          console.warn(`Skipping database save for unknown node type: ${nodeId}`);
        }
      } catch (error) {
        console.error(`Failed to save node ${nodeId} to database:`, error);
        // Continue silently - don't interrupt user experience
      }
    }

    } finally {
      autoLayoutRunning.current = false;
    }
    }, [setNodes, edges, setEdges, resolveWorkflowId, updateNode, reactFlowInstance]);

  return (
    <Panel
      position="bottom-left"
      className={`zoom-controls-panel ${
        shiftState !== "none" ? `shifted ${shiftState}` : ""
      }`}
    >
      <div
        className={`zoom-controls-container ${
          shiftState !== "none" ? `shifted ${shiftState}` : ""
        }`}
      >
        <div className="zoom-controls zoom-controls-primary dark:bg-card dark:text-white dark:border-none">
          <button className="zoom-button dark:bg-card dark:text-white dark:border-none" onClick={zoomOut} title="Zoom Out">
            <Minus size={16} />
          </button>
          <div className="zoom-level dark:text-white">{zoomLevel}%</div>
          <button className="zoom-button dark:bg-card dark:text-white dark:border-none" onClick={zoomIn} title="Zoom In">
            <Plus size={16} />
          </button>
          <div className="zoom-controls-divider"></div>
          <button className="zoom-button dark:text-white" onClick={autoLayoutSteps} title="Auto Layout Steps - Arrange connected steps vertically in chains below trigger">
            <AlignVerticalJustifyStart size={16} />
          </button>
        </div>

        <div className="zoom-controls zoom-controls-secondary dark:bg-card dark:text-white dark:border-none">
          <button
            className="expand-all-btn"
            onClick={expandAllNodes}
            title="Expand all nodes"
            style={{ marginRight: 4 }}
          >
            <ChevronUp size={18} />
          </button>
          <button
            className="collapse-all-btn"
            onClick={collapseAllNodes}
            title="Collapse all nodes"
            style={{ marginRight: 4 }}
          >
            <ChevronDown size={18} />
          </button>
          {hasCopiedNode() && (
            <button
              className="paste-btn"
              onClick={handlePaste}
              title={`Paste ${getCopiedNodeInfo()?.nodeName || 'node'}`}
            >
              <Clipboard size={18} />
            </button>
          )}
          {/* <button className="zoom-button" title="More Options">
            <MoreHorizontal size={16} />
          </button> */}
        </div>
      </div>
      <style>{`
        :global(.zoom-controls-panel.shifted) {
          transition: transform 0.3s ease;
        }

        :global(.zoom-controls-panel.shifted.single) {
          transform: translateX(280px);
        }

        :global(.zoom-controls-panel.shifted.history) {
          transform: translateX(420px);
        }

        :global(.zoom-controls-panel.shifted.double) {
          transform: translateX(650px);
        }

        :global(.zoom-controls-panel.shifted.double-history) {
          transform: translateX(790px);
        }

        :global(.zoom-controls-panel) {
          transition: transform 0.3s ease;
        }
      `}</style>
    </Panel>
  );
};

export default ZoomControls;
